import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import localeCaES from '@angular/common/locales/ca';
import localeEsES from '@angular/common/locales/es';
import localeFrFR from '@angular/common/locales/fr';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  BrowserModule,
  Meta,
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { httpInterceptorProviders } from '@api/interceptors';
import { environment } from '@environments/environment';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { Time } from '@shared/constants';
import { INDEXED_DB_CONFIG, IndexedDbService } from '@shared/services';
import { CommonService } from '@shared/services/common';
import { LocalizedCommon } from '@shared/services/common/models';
import { CustomTranslateLoader, LANGUAGE_FALLBACK } from '@shared/services/language';
import { GlobalErrorHandler, LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { RoutePopinDispatcherService } from '@shared/services/route-popin-dispatcher';
import { LocalizedCommonStore } from '@stores';
import { filter, firstValueFrom, map, of, switchMap, take, timeout } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { APP_ROUTES } from './app.routes';

registerLocaleData(localeFrFR);
registerLocaleData(localeEsES);
registerLocaleData(localeCaES);

const ROBOTS_INDEX = 'index, follow';
const ROBOTS_NOINDEX = 'noindex, follow, nosnippet, noimageindex, notranslate';

function updateRobotsMeta(meta: Meta, content: string): void {
  meta.removeTag("name='robots'");
  meta.addTag({ name: 'robots', content });
}

function initializeMetaRobots(): void {
  const meta = inject(Meta);
  const robotsContent = environment.allowIndexing ? ROBOTS_INDEX : ROBOTS_NOINDEX;
  updateRobotsMeta(meta, robotsContent);
}

function initializeApp(): () => Promise<void> {
  return async (): Promise<void> => {
    const logsService = inject(LogsService);
    const indexedDbService = inject(IndexedDbService);
    const commonService = inject(CommonService);
    const localizedCommonStore = inject(LocalizedCommonStore);
    const logs = inject(LogsService);

    const isPrefetch = environment.isPrefetchCmsEnabled;

    const load$ = (
      isPrefetch
        ? commonService.readPrefetch<LocalizedCommon>('common', { cacheExpirationTime: Time.TEN_MINUTES })
        : commonService.read('', {}, { cacheExpirationTime: Time.TEN_MINUTES })
    ).pipe(
      take(1),
      timeout({ each: 5000, with: () => of<LocalizedCommon | null>(null) }),
      catchError((err: unknown) => {
        logs.error(
          'initializeApp',
          `Unable to load common data after timeout (5s). This may affect core functionality.`,
          { error: err, isPrefetchEnabled: isPrefetch }
        );
        return of<LocalizedCommon | null>(null);
      })
    );

    logsService.updateLogLevelFromSession();

    try {
      await firstValueFrom(
        indexedDbService.connectToDB().pipe(
          filter((ok) => ok),
          take(1),
          switchMap(() => load$),
          tap((localizedCommon) => {
            if (localizedCommon) localizedCommonStore.set(localizedCommon);
          }),
          map(() => void 0)
        )
      );
    } catch (err) {
      logs.error(
        'initializeApp',
        'App initialization failed, continuing in degraded mode (IndexedDB/common data unavailable)',
        err
      );
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule),
    provideTranslateService({
      fallbackLang: LANGUAGE_FALLBACK,
      loader: provideTranslateLoader(CustomTranslateLoader),
    }),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideClientHydration(withIncrementalHydration(), withEventReplay()),
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'disabled',
      }),
      withComponentInputBinding()
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Zoneless: without these listeners, window 'error' and 'unhandledrejection' events never reach the ErrorHandler
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideAppInitializer(initializeMetaRobots),
    provideAppInitializer(() => {
      const isServer = inject(PlatformContextService).isServer();
      if (!isServer) inject(RoutePopinDispatcherService).initialize();
    }),
    provideAppInitializer(() => initializeApp()()),
    {
      provide: INDEXED_DB_CONFIG,
      useValue: ['socooc', 1], // [IndexDB database name, Database version]
    },
    ...httpInterceptorProviders,
  ],
};
